import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { useBranchStore } from '@/stores/branchStore';
import { useFeatureFlagsStore } from '@/stores/featureFlagsStore';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import BankingFlowRecap from '@/components/common/BankingFlowRecap';
import GettingStartedChecklist from '@/components/common/GettingStartedChecklist';
import ConvertToExpenseDialog from '@/components/banking/ConvertToExpenseDialog';
import TileCard from '@/components/banking/TileCard';
import { PlatformIbanBanner } from '@/components/banking/PlatformIbanBanner';
// iter418 — Quick Transfer dialog
import { QuickTransferDialog } from '@/components/banking/QuickTransferDialog';
import { Switch } from '@/components/ui/switch';
import { useAuthStore } from '@/stores';
import { ReconcileMonthDialog } from '@/components/banking/ReconcileMonthDialog';
import { AutoReconcileSettingsDialog } from '@/components/banking/AutoReconcileSettingsDialog';
import BankPaymentDialog from '@/components/BankPaymentDialog';
import ShareViewButton from '@/components/common/ShareViewButton';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { BranchFilter } from '@/components/filters/BranchFilter';
import { ReconReviewQueueCard } from '@/components/banking/ReconReviewQueueCard';
import {
  Banknote, ArrowDownToLine, ArrowUpFromLine, Truck, Users, Briefcase, Building2,
  Zap,
  ShoppingBag, Tag, RefreshCw, Filter, Link as LinkIcon, FileWarning, AlertTriangle, Download, Inbox, Plus, Calendar, Layers, Search, X,
} from 'lucide-react';
import { toast } from 'sonner';

const PERIODS = [
  { v: 'today', l: 'Today' },
  { v: 'week', l: '7 days' },
  { v: 'month', l: '30 days' },
  { v: 'quarter', l: '90 days' },
  { v: '180d', l: '6 months' },
  { v: 'year', l: 'Year' },
  { v: '24m', l: '24 months' },
  { v: 'all', l: 'All time' },
  { v: 'custom', l: 'Custom range…' }, // iter459 — reveals start/end date inputs
];

const KIND_META = {
  supplier: { icon: Truck, color: 'amber', label: 'Supplier' },
  employee: { icon: Users, color: 'sky', label: 'Employee' },
  partner: { icon: Briefcase, color: 'violet', label: 'Partner' },
  expense_category: { icon: Tag, color: 'rose', label: 'Expense' },
  customer: { icon: Building2, color: 'emerald', label: 'Customer' },
  platform: { icon: ShoppingBag, color: 'orange', label: 'Platform' },
  other: { icon: Banknote, color: 'stone', label: 'Other' },
};

// iter392 — subtype filter pills per kind. These mirror the SUBTYPES
// map in BankingQuickLinkPage so the labels match what users saw when
// they linked these rows. Pills are only shown when the user has
// selected a single `kind` (filtering by subtype across kinds is
// meaningless — "salary" doesn't apply to suppliers).
const KIND_SUBTYPES = {
  employee: [
    { v: 'salary', l: 'Salary' },
    { v: 'advance', l: 'Advance' },
    { v: 'eid_bonus', l: 'EID / Bonus' },
    { v: 'penalty', l: 'Penalty' },
    { v: 'reimbursement', l: 'Reimburse' },
    { v: 'advance_returned', l: 'Advance Returned' },
  ],
  partner: [
    { v: 'draw', l: 'Owner Draw' },
    { v: 'capital_return', l: 'Capital Return' },
    { v: 'loan_repayment', l: 'Loan Repayment' },
    { v: 'salary', l: 'Salary' },
    { v: 'expense_reimbursement', l: 'Reimburse' },
    { v: 'capital_injection', l: 'Capital In' },
  ],
  supplier: [
    { v: 'bill_payment', l: 'Bill Payment' },
    { v: 'advance_payment', l: 'Advance' },
    { v: 'deposit', l: 'Deposit' },
    { v: 'refund', l: 'Refund (incoming)' },
  ],
  customer: [
    { v: 'payment', l: 'Payment' },
    { v: 'advance', l: 'Advance' },
    { v: 'refund', l: 'Refund' },
    { v: 'rebate', l: 'Rebate' },
    { v: 'other_income', l: 'Other Income' },
  ],
  expense_category: [
    { v: 'general', l: 'General' },
    { v: 'fine', l: 'Fine / Penalty' },
    { v: 'asset_purchase', l: 'Asset Purchase' },
    { v: 'utility', l: 'Utility' },
    { v: 'rent', l: 'Rent' },
    { v: 'insurance', l: 'Insurance' },
    { v: 'fuel', l: 'Fuel / Vehicle' },
  ],
};

/**
 * Banking Payments Hub — single screen showing every bank-affecting
 * entry across the tenant. Aggregates `bank_payments` (outgoing &
 * incoming manual entries), bank-paid `expenses`, and incoming
 * `platform_payments`. Each row carries linkage info so the user
 * sees if the entry came from a bank-statement match, a manual
 * "Pay from bank" click, or was recorded standalone.
 *
 * Why one page: the user previously had to bounce between
 * /bank-payments, /supplier-payments, /expenses, /bank-statements
 * to see who got paid from which account. This Hub consolidates.
 */
export default function BankingHubPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  // Advanced Mode — hides the complex banking controls (auto-reconcile
  // scheduling, bulk group-link, subtype flag strips) from the standard
  // view. The toggle itself is only visible to the Super Admin.
  const [advancedMode, setAdvancedMode] = useState(false);
  const showAdvanced = !!user?.is_super_admin && advancedMode;
  // Hydrate filter state from URL — lets a shared link land on the
  // exact same view (period / direction / branch / account / kind).
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [direction, setDirection] = useState(searchParams.get('direction') || 'all');
  const [kind, setKind] = useState(searchParams.get('kind') || 'all');
  // iter392 — subtype filter is conditional on the chosen kind. Resets
  // automatically when the user switches kinds.
  const [subtype, setSubtype] = useState(searchParams.get('subtype') || 'all');
  const [period, setPeriod] = useState(searchParams.get('period') || 'month');
  const [accountId, setAccountId] = useState(searchParams.get('account') || 'all');
  const [branchId, setBranchId] = useState(searchParams.get('branch') || 'all');
  // iter458 — Source filter: lets the user narrow to ledger rows that
  // originated from a bank-statement match vs. manually-entered vs.
  // orphan (statement was deleted). Filtered client-side from the
  // existing `r.status` / `r.source` fields — no backend change needed.
  const [sourceFilter, setSourceFilter] = useState(searchParams.get('source') || 'all');
  // iter459 — Custom date range + free-text search.
  // `startDate` / `endDate` map directly to the new backend params; they're
  // only meaningful when period === 'custom'. `searchText` is applied
  // client-side against party_name, reference, notes & bank-name.
  const [startDate, setStartDate] = useState(searchParams.get('start') || '');
  const [endDate, setEndDate] = useState(searchParams.get('end') || '');
  const [searchText, setSearchText] = useState(searchParams.get('q') || '');
  const [accounts, setAccounts] = useState([]);
  const { branches, fetchBranches } = useBranchStore();

  // Inline "Convert to expense" modal state. We open it on demand from the
  // ledger row's "+ Expense" button — see `convertToExpense` below.
  const [convertRow, setConvertRow] = useState(null);
  const [convertCategory, setConvertCategory] = useState('');
  const [convertVendor, setConvertVendor] = useState('');
  // iter527 — picked supplier id from the new searchable supplier combobox
  const [convertSupplierId, setConvertSupplierId] = useState('');
  const [convertNotes, setConvertNotes] = useState('');
  const [convertSaving, setConvertSaving] = useState(false);
  const [expenseCategories, setExpenseCategories] = useState([]);
  // New bank-payment dialog — opens from the header button. Previously
  // the button was a bare `<Link to="/bank-payments">` which had no
  // matching route and fell back to the dashboard redirect. We now
  // reuse the shared BankPaymentDialog component instead.
  const [showNewPayment, setShowNewPayment] = useState(false);
  // iter418 — Quick Transfer dialog: account-number lookup → auto-post
  const [showQuickTransfer, setShowQuickTransfer] = useState(false);
  // iter425 — "Reconcile this whole month" dialog state
  const [showReconcileMonth, setShowReconcileMonth] = useState(false);
  // iter426 — Auto-Reconcile settings dialog state
  const [showAutoReconSettings, setShowAutoReconSettings] = useState(false);
  // iter395 — Counts used by GettingStartedChecklist auto-completion so
  // the user doesn't see "Add at least one bank account" when they
  // already have accounts/statements. Fetched once on mount.
  const [setupCounts, setSetupCounts] = useState({
    bank_accounts: 0,
    bank_statements: 0,
    rules: 0,
    inbox_cleared: 0,
    bank_payments: 0,
  });
  // iter390 — Unlink dialog state. When the user clicks the new amber
  // "Unlink" button on a row that has downstream links, we fetch the
  // list of linked docs and let them remove (delete) or detach (keep).
  const [unlinkRow, setUnlinkRow] = useState(null);
  const [unlinkLinks, setUnlinkLinks] = useState([]);
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [unlinkBusy, setUnlinkBusy] = useState(false);

  // Convert an outgoing bank_payment to a Pass-Through Advance.
  // Uses the existing payee_id as the advance recipient so no UI dialog needed.
  const convertToAdvance = async (row) => {
    if (!row.payee_id) {
      toast.error('Link this payment to an employee/partner first (via Quick-Link), then convert to Advance.');
      return;
    }
    const confirmed = window.confirm(
      `Create Pass-Through Advance?\n\nRecipient: ${row.party_name || row.payee_id}\nAmount: ${cur()} ${row.amount?.toFixed(2) || '0.00'}\nDate: ${(row.ts || '').slice(0, 10)}\n\nThis creates an advance record that can be later reconciled with actual expenses.`,
    );
    if (!confirmed) return;
    try {
      await api.post('/pass-through-advances', {
        person_type: row.kind,
        person_id: row.payee_id,
        paying_branch_id: row.paying_branch_id,
        paying_bank_account_id: row.bank_account_id,
        payment_mode: row.bank_account_id ? 'bank' : 'cash',
        amount: row.amount,
        date: (row.ts || '').slice(0, 10),
        description: row.description || 'Advance from Banking Hub',
        reference: row.reference,
        bank_payment_id: row.id,
      });
      toast.success(`Advance created for ${row.party_name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create advance');
    }
  };

  // "Save as Rule" — teach the auto-matcher from a real linked example.
  const saveAsRule = async (row) => {
    const confirmed = window.confirm(
      `Create an auto-rule?\n\nNext time a bank transaction's description contains "${(row.description || row.reference || '').slice(0, 40)}", it will auto-link to:\n  → ${row.party_name || row.kind}\n\nYou can edit or delete this rule later in Banking Rules.`,
    );
    if (!confirmed) return;
    try {
      const r = await api.post('/banking/rules/from-payment', { bp_id: row.id, match_on: 'description', match_type: 'contains', direction_only: true });
      toast.success(`Rule "${r.data.name}" created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create rule');
    }
  };

  const openUnlinkDialog = async (row) => {
    setUnlinkRow(row);
    setUnlinkLinks([]);
    setUnlinkLoading(true);
    try {
      const r = await api.get(`/banking/quick-link/links/${row.id}`);
      setUnlinkLinks(r.data?.links || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load linked records');
    } finally {
      setUnlinkLoading(false);
    }
  };

  const performUnlink = async (link, deleteDownstream) => {
    if (!unlinkRow || !link) return;
    if (!window.confirm(deleteDownstream
      ? `Delete the ${link.collection.replace('_', ' ')} record for ${link.name} (SAR ${(link.amount || 0).toFixed(2)})? This cannot be undone.`
      : `Detach the ${link.collection.replace('_', ' ')} record for ${link.name}? The record stays in your books, only the bank link is removed.`
    )) return;
    setUnlinkBusy(true);
    try {
      await api.post('/banking/quick-link/unlink', {
        row_type: 'bank_payment',
        row_id: unlinkRow.id,
        downstream_collection: link.collection,
        downstream_id: link.id,
        delete_downstream: deleteDownstream,
      });
      toast.success(deleteDownstream ? 'Linked record deleted' : 'Bank link removed');
      setUnlinkLinks((prev) => prev.filter((l) => l.id !== link.id));
      // If this was the last link, close the dialog and refresh the hub.
      if (unlinkLinks.length <= 1) {
        setUnlinkRow(null);
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unlink failed');
    } finally {
      setUnlinkBusy(false);
    }
  };


  useEffect(() => {
    fetchBranches();
    api.get('/bank-accounts').then(r => setAccounts(r.data || [])).catch(() => {});
    // Pre-fetch expense categories so the convert modal can show a dropdown
    // without an extra round-trip when the user clicks "+ Expense".
    api.get('/categories?category_type=expense')
      .then(r => setExpenseCategories(Array.isArray(r.data) ? r.data : (r.data?.data || [])))
      .catch(() => setExpenseCategories([]));
    // iter395 — Hydrate setup counts so the GettingStartedChecklist
    // auto-completes already-done items (instead of telling the user
    // to "Add at least one bank account" when they have 5).
    Promise.all([
      api.get('/bank-accounts').then(r => (r.data || []).length).catch(() => 0),
      api.get('/bank-statements').then(r => (r.data || []).length).catch(() => 0),
      api.get('/banking/rules').then(r => (r.data?.rules || []).length).catch(() => 0),
      // Inbox-cleared: we treat the step as done if there are at least
      // some linked bank_payments (signal that the user has linked
      // a bank txn at least once).
      api.get('/banking/ledger', { params: { period: 'all', limit: 1 } })
        .then(r => (r.data?.total_count ?? r.data?.count ?? 0))
        .catch(() => 0),
      // iter395h — Bank payments ever recorded by this tenant. Used by
      // the GettingStartedChecklist auto-complete to keep the "Record a
      // new bank payment" CTA clickable until at least one exists, then
      // mark it done permanently (without ever hiding the CTA, since
      // recurring use is expected).
      api.get('/bank-payments', { params: { limit: 1 } })
        .then(r => (Array.isArray(r.data) ? r.data.length : (r.data?.count || (r.data?.payments || []).length || 0)))
        .catch(() => 0),
    ]).then(([bank_accounts, bank_statements, rules, inbox_cleared, bank_payments]) => {
      setSetupCounts({ bank_accounts, bank_statements, rules, inbox_cleared, bank_payments });
    });
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [direction, kind, subtype, period, accountId, branchId, startDate, endDate]);

  // Reset subtype when the kind changes (a 'salary' subtype only
  // makes sense when kind=employee).
  useEffect(() => {
    setSubtype('all');
  }, [kind]);

  // Mirror current filter state into the URL so the address bar always
  // reflects what the user is looking at — that's what ShareViewButton
  // copies to clipboard. Skips identity values so we don't pollute
  // the URL with `?period=month` (the default).
  useEffect(() => {
    const next = {};
    if (direction !== 'all') next.direction = direction;
    if (kind !== 'all') next.kind = kind;
    if (subtype !== 'all') next.subtype = subtype;
    if (period !== 'month') next.period = period;
    if (accountId !== 'all') next.account = accountId;
    if (branchId !== 'all') next.branch = branchId;
    if (sourceFilter !== 'all') next.source = sourceFilter;
    if (period === 'custom' && startDate) next.start = startDate;
    if (period === 'custom' && endDate) next.end = endDate;
    if (searchText.trim()) next.q = searchText.trim();
    setSearchParams(next, { replace: true });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [direction, kind, subtype, period, accountId, branchId, sourceFilter, startDate, endDate, searchText]);

  // iter394 — One-click export of the filtered ledger view. Uses the
  // same filter set as the live load, requests the file as a blob so
  // the JWT-bearing axios instance handles auth, and triggers a
  // download via an object URL. Generic enough to be reused by any
  // future "filtered view" exports on this page.
  const exportLedger = async (fmt /* 'csv' | 'xlsx' */) => {
    const params = { fmt, period, limit: 5000 };
    if (direction !== 'all') params.direction = direction;
    if (kind !== 'all') params.payee_type = kind;
    if (subtype !== 'all') params.subtype = subtype;
    if (accountId !== 'all') params.bank_account_id = accountId;
    if (branchId !== 'all') params.branch_id = branchId;
    if (period === 'custom') {
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
    }
    try {
      const r = await api.get('/banking/ledger/export', { params, responseType: 'blob' });
      // Prefer the server-supplied filename (encodes the active filters).
      const cd = r.headers?.['content-disposition'] || '';
      const m = /filename=([^;]+)/i.exec(cd);
      const fname = (m && m[1].trim().replace(/^"|"$/g, '')) || `banking_ledger.${fmt}`;
      const blob = new Blob([r.data], {
        type: fmt === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Downloaded ${fname}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Export failed (${fmt})`);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { period, limit: 500 };
      if (direction !== 'all') params.direction = direction;
      if (kind !== 'all') params.payee_type = kind;
      if (subtype !== 'all') params.subtype = subtype;
      if (accountId !== 'all') params.bank_account_id = accountId;
      if (branchId !== 'all') params.branch_id = branchId;
      // iter459 — only forward custom range when the user picked it.
      if (period === 'custom') {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      const r = await api.get('/banking/ledger', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load banking ledger');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // iter458 — Apply Source filter client-side. Mirrors the same status
  // classification logic used by the row badge below so the dropdown
  // value always matches what the user sees rendered.
  const classifySource = (r) => {
    if (r.status === 'orphan_statement') return 'orphan';
    if (r.status === 'linked') return 'linked';
    if (r.source === 'expense_first') return 'expense_first';
    return 'manual';
  };
  const allRows = data?.rows || [];
  // iter459 — Client-side free-text search across the visible columns.
  // Case-insensitive contains over party_name / reference / notes / bank.
  const q = searchText.trim().toLowerCase();
  const searched = q
    ? allRows.filter((r) => {
        const hay = [
          r.party_name, r.reference, r.notes, r.bank_account_name,
          r.subtype, r.kind,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      })
    : allRows;
  const rows = sourceFilter === 'all'
    ? searched
    : searched.filter((r) => classifySource(r) === sourceFilter);
  const totals = data?.totals || {};

  // Open the inline conversion modal — prefills vendor + notes from the
  // bank-payment row so the user only has to pick a category in the
  // common case.
  const convertToExpense = (row) => {
    setConvertRow(row);
    setConvertCategory('');
    setConvertVendor(row.party_name || '');
    setConvertNotes(row.notes || row.reference || '');
  };

  const submitConvert = async () => {
    if (!convertRow) return;
    if (!convertCategory.trim()) {
      toast.error('Pick or type a category');
      return;
    }
    setConvertSaving(true);
    try {
      await api.post(`/banking/payments/${convertRow.id}/convert-to-expense`, {
        category: convertCategory.trim(),
        vendor: convertVendor.trim(),
        // iter527 — link to existing supplier when the user picked one from the combobox
        supplier_id: convertSupplierId || null,
        description: convertNotes.trim(),
      });
      toast.success(convertSupplierId
        ? 'Expense created, linked to the bank payment and the supplier ledger'
        : 'Expense created and linked to this bank payment');
      setConvertRow(null);
      setConvertSupplierId('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="banking-hub">
        <SettingsPageHeader
          icon={Building2}
          title="Banking Hub"
          subtitle="Your banking control room — every payment that touched your bank, in one filterable view."
          accent="sky"
        />
        {/* What is this page? — collapsible orientation help. */}
        <PageInfoCallout
          storageKey="banking-hub"
          title="What is the Banking Hub?"
        >
          <p>
            <strong>Your banking control room.</strong> Every payment that touched your bank — incoming settlements,
            outgoing supplier/employee/partner payments, expenses, platform payouts — shown in one filterable list.
            Start every morning here: see what&apos;s matched, what still needs attention, and jump into the right
            tool to fix it. Use the <strong>+ Expense</strong> action to turn any unlinked outgoing payment into a
            categorised expense in one click.
          </p>
        </PageInfoCallout>
        <BankingFlowRecap active="/banking-hub" />

        {/* iter400 — Detected new platform IBAN banner. Renders only
            when the backend finds incoming bank txns whose IBAN isn't
            yet on the matched platform. One click teaches the system. */}
        <PlatformIbanBanner days={90} testidPrefix="hub-plat-iban" />

        {/* Getting-started checklist — helps first-time users activate
            the Banking Hub workflow in a few guided clicks. Dismissible,
            auto-hides when all steps are complete. */}
        <GettingStartedChecklist
          storageKey="banking-hub-v1"
          title="Activate Banking Hub"
          isCompleted={(step) => {
            switch (step.key) {
              case 'bank-account': return setupCounts.bank_accounts > 0;
              case 'upload-statement': return setupCounts.bank_statements > 0;
              case 'check-queue': return setupCounts.inbox_cleared > 0;
              case 'auto-rules': return setupCounts.rules > 0;
              case 'new-payment': return setupCounts.bank_payments > 0;
              default: return false;
            }
          }}
          steps={[
            { key: 'bank-account', label: 'Add at least one bank account', href: '/bank-accounts', cta: 'Open', auto: true },
            { key: 'upload-statement', label: 'Upload your first bank statement (PDF / Excel)', href: '/bank-statements', cta: 'Upload', auto: true },
            { key: 'new-payment', label: 'Record a new bank payment to a supplier', onClick: () => setShowNewPayment(true), cta: 'New payment', auto: true },
            { key: 'check-queue', label: 'Clear your first Quick-link inbox row', href: '/banking-quick-link', cta: 'Open inbox', auto: true },
            { key: 'auto-rules', label: 'Set up your first auto-categorization rule', href: '/banking-rules', cta: 'Create rule', auto: true },
          ]}
        />

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit">Banking Hub</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Every payment that touched the bank — incoming, outgoing, supplier, employee, partner, expense — in one place.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ShareViewButton
              params={{ direction, kind, subtype, period, account: accountId, branch: branchId }}
            />
            <Button variant="outline" size="sm" onClick={load} className="rounded-xl" data-testid="banking-hub-refresh">
              <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {/* iter394 — Export the currently filtered view as CSV or
                Excel. The endpoint mirrors every filter from /banking/
                ledger so the file matches the table on screen exactly.
                Uses an axios blob fetch so the auth header is included
                — a plain href would skip the JWT. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportLedger('csv')}
              disabled={loading}
              className="rounded-xl"
              data-testid="banking-hub-export-csv"
              title="Export current view as CSV"
            >
              <Download size={13} className="mr-1.5" /> CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportLedger('xlsx')}
              disabled={loading}
              className="rounded-xl"
              data-testid="banking-hub-export-xlsx"
              title="Export current view as Excel"
            >
              <Download size={13} className="mr-1.5" /> Excel
            </Button>
            <Link to="/bank-payments" onClick={(e) => e.preventDefault()}>
              <Button
                className="bg-primary hover:bg-primary/90 rounded-xl"
                data-testid="banking-hub-new-payment"
                onClick={() => setShowNewPayment(true)}
              >
                <Banknote size={14} className="mr-1.5" /> New Bank Payment
              </Button>
            </Link>
            {/* iter418 — Quick Transfer: type account # → auto-detect
                supplier/employee/partner/customer/platform → post in 1 click */}
            <Button
              variant="outline"
              className="rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="banking-hub-quick-transfer"
              onClick={() => setShowQuickTransfer(true)}
            >
              <Zap size={14} className="mr-1.5" /> Quick Transfer
            </Button>
            {user?.is_super_admin && (
              <div className="flex items-center gap-1.5 mr-1" data-testid="banking-hub-advanced-toggle">
                <Switch checked={advancedMode} onCheckedChange={setAdvancedMode} id="banking-adv-mode" />
                <label htmlFor="banking-adv-mode" className="text-xs text-muted-foreground cursor-pointer select-none">Advanced</label>
              </div>
            )}
            {/* iter425 — Reconcile This Whole Month: surface every
                unlinked txn for a window grouped by best-guess payee */}
            {showAdvanced && (
            <Button
              variant="outline"
              className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="banking-hub-reconcile-month"
              onClick={() => setShowReconcileMonth(true)}
            >
              <RefreshCw size={14} className="mr-1.5" /> Reconcile Month
            </Button>
            )}
            {/* iter426 — Schedule the same scan automatically */}
            {showAdvanced && (
            <Button
              variant="outline"
              className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="banking-hub-auto-recon-settings"
              onClick={() => setShowAutoReconSettings(true)}
              title="Configure scheduled auto-reconciliation"
            >
              <Calendar size={14} className="mr-1.5" /> Auto-Reconcile
            </Button>
            )}
            {showAdvanced && (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="banking-hub-group-link-link"
              title="Bulk-link unlinked bank lines that share a payee"
            >
              <Link to="/banking-group-link">
                <Layers size={14} className="mr-1.5" /> Group &amp; Bulk Link
              </Link>
            </Button>
            )}
          </div>
        </div>

        {/* Safety reassurance banner — explains what won't break.
            Most users worry about double entries / cascade-deletes. */}
        {showAdvanced && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/10 dark:border-emerald-900 p-3 text-xs flex items-start gap-2.5">
          <FileWarning size={14} className="text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-emerald-800 dark:text-emerald-300">
            <strong className="font-semibold">Safe by design:</strong> deleting a bank statement{' '}
            <em>never</em> removes payments you already linked from it. Re-uploading the same
            file is automatically de-duplicated by SHA-256 hash and re-attaches any orphaned links.
          </div>
        </div>
        )}

        {/* Inbox count banner — explains the gap between Banking Hub totals
            and Bank Statements totals (the latter shows raw imported lines,
            this page shows only categorized ones). When the user has untagged
            statement txns, surfacing them with a 1-click CTA closes the gap. */}
        <InboxCountBanner bankAccountId={accountId !== 'all' ? accountId : null} />

        {/* Totals tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <TileCard icon={<ArrowDownToLine size={14} />} label="Incoming" value={fmt(totals.incoming)} tone="emerald" testid="banking-hub-incoming" />
          <TileCard icon={<ArrowUpFromLine size={14} />} label="Outgoing" value={fmt(totals.outgoing)} tone="rose" testid="banking-hub-outgoing" />
          <TileCard icon={<LinkIcon size={14} />} label="Total Entries" value={(data?.total_count ?? data?.count ?? 0).toString()} sub={data?.total_count && data?.count && data.total_count > data.count ? `(showing ${data.count})` : null} tone="sky" testid="banking-hub-count" />
          <TileCard icon={<AlertTriangle size={14} />} label="Net Movement" value={fmt((totals.incoming || 0) - (totals.outgoing || 0))} tone="violet" testid="banking-hub-net" />
        </div>

        {/* By-kind breakdown chips */}
        {totals.by_kind && Object.keys(totals.by_kind).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(totals.by_kind).map(([k, v]) => {
              const meta = KIND_META[k] || KIND_META.other;
              const Icon = meta.icon;
              return (
                <button
                  key={k}
                  onClick={() => setKind(k === kind ? 'all' : k)}
                  data-testid={`banking-hub-kind-chip-${k}`}
                  className={`px-3 py-1.5 rounded-lg border text-xs flex items-center gap-1.5 transition-all ${
                    kind === k
                      ? `bg-${meta.color}-500 text-white border-${meta.color}-600`
                      : `bg-${meta.color}-50 text-${meta.color}-700 border-${meta.color}-200 hover:bg-${meta.color}-100`
                  }`}
                >
                  <Icon size={11} /> {meta.label}: {v.count} · {fmt(v.amount)}
                </button>
              );
            })}
          </div>
        )}

        {/* iter392 — Subtype filter pills, only shown when a single
            kind is selected. Lets the user drill into "show me only
            employee bonuses this quarter" with one click. */}
        {kind !== 'all' && KIND_SUBTYPES[kind] && (
          <div className="flex flex-wrap items-center gap-1.5 -mt-1" data-testid="banking-hub-subtype-pills">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mr-1">
              Type:
            </span>
            <button
              type="button"
              onClick={() => setSubtype('all')}
              data-testid="banking-hub-subtype-all"
              className={`px-2.5 py-1 rounded-full border text-[11px] transition-colors ${
                subtype === 'all'
                  ? 'bg-stone-800 text-white border-stone-900'
                  : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-100'
              }`}
            >
              All
            </button>
            {KIND_SUBTYPES[kind].map((s) => (
              <button
                key={s.v}
                type="button"
                onClick={() => setSubtype(subtype === s.v ? 'all' : s.v)}
                data-testid={`banking-hub-subtype-${s.v}`}
                className={`px-2.5 py-1 rounded-full border text-[11px] transition-colors ${
                  subtype === s.v
                    ? `bg-${(KIND_META[kind] || {color:'stone'}).color}-600 text-white border-${(KIND_META[kind] || {color:'stone'}).color}-700`
                    : `bg-${(KIND_META[kind] || {color:'stone'}).color}-50 text-${(KIND_META[kind] || {color:'stone'}).color}-700 border-${(KIND_META[kind] || {color:'stone'}).color}-200 hover:bg-${(KIND_META[kind] || {color:'stone'}).color}-100`
                }`}
              >
                {s.l}
              </button>
            ))}
          </div>
        )}

        {/* iter393 — Subtype totals strip. Renders compact per-subtype
            count + amount summaries when a single kind is selected and
            ≥1 subtype has data. Lives between the pill row and the
            ledger filter bar so the user sees their compensation/spend
            distribution at a glance — especially useful right before
            payroll. Uses the kind's own color for visual continuity. */}
        {showAdvanced && kind !== 'all' && totals.by_subtype && Object.keys(totals.by_subtype).length > 0 && (() => {
          const meta = KIND_META[kind] || { color: 'stone' };
          // Look up the human-readable label from KIND_SUBTYPES so the
          // strip matches the pill labels above.
          const labelFor = (v) => (KIND_SUBTYPES[kind]?.find((s) => s.v === v)?.l) || v.replace(/_/g, ' ');
          const sorted = Object.entries(totals.by_subtype).sort((a, b) => (b[1].amount || 0) - (a[1].amount || 0));
          return (
            <div
              className="flex flex-wrap items-center gap-x-3 gap-y-1.5 -mt-1 px-3 py-2 rounded-xl bg-stone-50 border border-stone-200"
              data-testid="banking-hub-subtype-totals"
            >
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                Distribution
              </span>
              {sorted.map(([k, v]) => (
                <div
                  key={k}
                  className={`inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded-md cursor-pointer transition-colors ${
                    subtype === k
                      ? `bg-${meta.color}-100 ring-1 ring-${meta.color}-300`
                      : 'hover:bg-white'
                  }`}
                  onClick={() => setSubtype(subtype === k ? 'all' : k)}
                  data-testid={`subtype-total-${k}`}
                  title={`Click to filter to ${labelFor(k)}`}
                >
                  <span className={`text-[11px] font-semibold text-${meta.color}-800`}>
                    {labelFor(k)}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {v.count}×
                  </span>
                  <span className={`text-[12px] font-bold tabular-nums text-${meta.color}-900`}>
                    {fmt(v.amount)}
                  </span>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Filter bar */}
        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <Filter size={13} className="text-muted-foreground" />
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="banking-hub-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All directions</SelectItem>
                <SelectItem value="incoming">Incoming only</SelectItem>
                <SelectItem value="outgoing">Outgoing only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="banking-hub-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                {Object.entries(KIND_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="banking-hub-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIODS.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* iter882 — Shared <DateRangeFilter> — provides preset popover
                (Today / 7 days / This month / 90 days / Year) and custom
                start+end date pickers. Switches period → 'custom' when the
                user chooses a range so downstream `startDate`/`endDate`
                params get sent to the backend. */}
            <DateRangeFilter
              value={{ start_date: startDate || null, end_date: endDate || null }}
              onChange={(r) => {
                setStartDate(r.start_date || '');
                setEndDate(r.end_date || '');
                if (r.start_date && r.end_date) setPeriod('custom');
                else if (!r.start_date && !r.end_date) setPeriod('all');
              }}
              testid="banking-hub-daterange"
            />
            {/* iter459 — Legacy custom date inputs preserved so users
                editing the URL params directly still see something. Hidden
                once <DateRangeFilter> owns the state (period !== 'custom'
                or when both fields empty). */}
            {period === 'custom' && (!startDate || !endDate) && (
              <>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-36 h-8 text-xs"
                  data-testid="banking-hub-start-date"
                  title="Start date (inclusive)"
                />
                <span className="text-[10px] text-muted-foreground">→</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-36 h-8 text-xs"
                  data-testid="banking-hub-end-date"
                  title="End date (inclusive)"
                />
              </>
            )}
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="banking-hub-account"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All bank accounts</SelectItem>
                {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name || a.bank_name}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* iter882 — Shared <BranchFilter>: consistent with POS, Payroll,
                Accounting, Stock etc. Emits `null` for All branches. */}
            <BranchFilter
              value={branchId === 'all' ? null : branchId}
              onChange={(v) => setBranchId(v || 'all')}
              width="w-36"
              testid="banking-hub-branch"
            />
            {/* iter458 — Source filter: narrow to ledger rows that came from
                bank-statement matches vs. manual entries vs. orphan (parent
                statement deleted) vs. expense-first (recorded standalone,
                awaiting a statement match). */}
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="banking-hub-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="linked">Linked from statement</SelectItem>
                <SelectItem value="manual">Manual entry</SelectItem>
                <SelectItem value="expense_first">Expense-first</SelectItem>
                <SelectItem value="orphan">Orphan (stmt deleted)</SelectItem>
              </SelectContent>
            </Select>
            {/* iter459 — Free-text search across party / reference / notes /
                bank-name / subtype / kind. Client-side: instant, no extra
                round-trip. Visual clear button mirrors typical search inputs. */}
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search party, reference, notes…"
                className="h-8 w-60 text-xs pl-7 pr-7"
                data-testid="banking-hub-search"
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => setSearchText('')}
                  data-testid="banking-hub-search-clear"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted transition-colors"
                  aria-label="Clear search"
                >
                  <X size={12} className="text-muted-foreground" />
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* iter883 — Recon Review Queue widget */}
        <ReconReviewQueueCard />

        {/* Ledger table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-outfit flex items-center gap-2">
              <Banknote size={14} className="text-primary" /> Payments Ledger
              <Badge variant="outline" className="ml-1 text-[10px] font-normal">{rows.length} entries</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="banking-hub-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Dir</th>
                    <th className="px-3 py-2 font-medium">Kind</th>
                    <th className="px-3 py-2 font-medium">Party</th>
                    <th className="px-3 py-2 font-medium">Bank</th>
                    <th className="px-3 py-2 font-medium">Reference</th>
                    <th className="px-3 py-2 font-medium text-right">Amount</th>
                    <th className="px-3 py-2 font-medium">Source</th>
                    <th className="px-3 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && !loading && (
                    <tr><td colSpan={9} className="px-3 py-12 text-center text-muted-foreground">
                      No bank entries in this period. Adjust filters or click "New Bank Payment" to add one.
                    </td></tr>
                  )}
                  {rows.map((r) => {
                    const meta = KIND_META[r.kind] || KIND_META.other;
                    const Icon = meta.icon;
                    return (
                      <tr key={`${r.raw_collection}-${r.id}`} className="border-b hover:bg-muted/40" data-testid={`banking-hub-row-${r.id}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-muted-foreground tabular-nums">
                          {r.ts ? new Date(r.ts).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {r.direction === 'incoming' ? (
                            <ArrowDownToLine size={13} className="text-emerald-600" />
                          ) : (
                            <ArrowUpFromLine size={13} className="text-rose-600" />
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-${meta.color}-100 text-${meta.color}-700 text-[10px] font-medium`}>
                              <Icon size={10} /> {meta.label}
                            </span>
                            {r.raw_collection === 'bank_payments' && (
                              <InlineKindDropdown row={r} onChanged={load} />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 font-medium">
                          {r.party_name}
                          {r.subtype && (
                            <span
                              className="ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide bg-stone-100 text-stone-700 border border-stone-200"
                              data-testid={`row-subtype-${r.id}`}
                              title={`Subtype: ${r.subtype}`}
                            >
                              {r.subtype.replace(/_/g, ' ')}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{r.bank_account_name || '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground font-mono text-[10px]">{r.reference || '—'}</td>
                        <td className={`px-3 py-2 text-right font-bold tabular-nums ${r.direction === 'incoming' ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {r.direction === 'incoming' ? '+' : '−'}{fmt(r.amount)}
                        </td>
                        <td className="px-3 py-2">
                          {r.status === 'orphan_statement' ? (
                            <span className="text-amber-700 text-[10px] font-medium" title="Statement was deleted; payment kept intact">
                              Statement deleted ⚠
                            </span>
                          ) : r.status === 'linked' ? (
                            <Link to={`/bank-statements?stmt=${r.source_id}`} className="text-sky-700 text-[10px] font-medium hover:underline" title="From bank statement match">
                              From statement →
                            </Link>
                          ) : r.source === 'expense_first' ? (
                            <span className="text-violet-700 text-[10px] font-medium" title="Expense recorded standalone, awaiting statement match">
                              Expense first
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-[10px]">Manual</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {/* Inline actions wrapped in a flex container so
                              there are no whitespace text-nodes between
                              buttons (silences the React `<span> child of
                              <tr>` hydration warning).
                              `bank_payments` direction=outgoing without
                              a downstream expense link → Convert to expense.
                              `expense_first` rows → Match to statement (deep-link). */}
                          <div className="inline-flex items-center gap-1 justify-end">
                            {r.raw_collection === 'bank_payments' && r.direction === 'outgoing' && !r.linked_downstream_ids?.length && (
                              <button
                                type="button"
                                onClick={() => convertToExpense(r)}
                                data-testid={`hub-convert-${r.id}`}
                                className="px-2 py-1 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-700 text-[10px] font-semibold border border-rose-200 transition-colors"
                                title="Create an expense record linked to this bank payment"
                              >
                                + Expense
                              </button>
                            )}
                            {/* Pass-Through Advance quick-link — on outgoing payments to
                                employees/partners, let the user create an Advance in one click
                                instead of navigating to the Pass-Through Advances page. */}
                            {r.raw_collection === 'bank_payments' && r.direction === 'outgoing' && !r.linked_downstream_ids?.length && (r.kind === 'employee' || r.kind === 'partner') && (
                              <button
                                type="button"
                                onClick={() => convertToAdvance(r)}
                                data-testid={`hub-advance-${r.id}`}
                                className="px-2 py-1 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[10px] font-semibold border border-emerald-200 transition-colors"
                                title="Create a Pass-Through Advance for this employee/partner"
                              >
                                + Advance
                              </button>
                            )}
                            {/* iter390 — Unlink. Visible whenever the row has
                                ≥1 downstream link. Clicking opens a confirm
                                dialog that lists each linked doc and lets
                                the user delete or detach. */}
                            {r.raw_collection === 'bank_payments' && (r.linked_downstream_ids?.length || 0) > 0 && (
                              <button
                                type="button"
                                onClick={() => openUnlinkDialog(r)}
                                data-testid={`hub-unlink-${r.id}`}
                                className="px-2 py-1 rounded-md bg-amber-50 hover:bg-amber-100 text-amber-700 text-[10px] font-semibold border border-amber-200 transition-colors"
                                title="View linked records / unlink if wrong"
                              >
                                Unlink
                              </button>
                            )}
                            {r.source === 'expense_first' && (
                              <Link
                                to={`/bank-statements?match_amount=${r.amount}&match_date=${r.ts?.slice(0, 10)}`}
                                className="px-2 py-1 rounded-md bg-violet-50 hover:bg-violet-100 text-violet-700 text-[10px] font-semibold border border-violet-200 transition-colors inline-block"
                                title="Find matching bank statement transaction"
                                data-testid={`hub-match-${r.id}`}
                              >
                                Match →
                              </Link>
                            )}
                            {r.raw_collection === 'platform_payments' && r.direction === 'incoming' && (
                              <Link
                                to={`/bank-statements?stmt=${r.source_id || ''}`}
                                className="px-2 py-1 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[10px] font-semibold border border-emerald-200 transition-colors inline-block"
                                title="View the bank statement this settlement belongs to"
                                data-testid={`hub-view-stmt-${r.id}`}
                              >
                                View
                              </Link>
                            )}
                            {/* "Save as Rule" — teach the auto-matcher from this linked example.
                                Visible on bank_payments rows that are already linked to an entity
                                (have a party_name). Next matching txn auto-links to the same target. */}
                            {r.raw_collection === 'bank_payments' && r.party_name && (r.linked_downstream_ids?.length || 0) > 0 && (
                              <button
                                type="button"
                                onClick={() => saveAsRule(r)}
                                data-testid={`hub-save-rule-${r.id}`}
                                className="px-2 py-1 rounded-md bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[10px] font-semibold border border-indigo-200 transition-colors"
                                title="Create an auto-rule so future matching txns link to the same target"
                              >
                                💡 Save as Rule
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Convert-to-expense modal — opened by the inline "+ Expense" button
          on outgoing bank-payment rows that have no downstream link yet.
          Vendor + notes are prefilled from the row; the user only has to
          pick or type a category in the typical flow. */}
      <ConvertToExpenseDialog
        row={convertRow}
        onOpenChange={(v) => { if (!v) { setConvertRow(null); setConvertSupplierId(''); } }}
        category={convertCategory}
        setCategory={setConvertCategory}
        vendor={convertVendor}
        setVendor={setConvertVendor}
        supplierId={convertSupplierId}
        setSupplierId={setConvertSupplierId}
        notes={convertNotes}
        setNotes={setConvertNotes}
        expenseCategories={expenseCategories}
        saving={convertSaving}
        onSubmit={submitConvert}
        formatAmount={fmt}
      />

      {/* Shared bank-payment creation dialog — handles all payee types
          (supplier/employee/partner/customer/platform/expense_category/
          ad_hoc) and per-branch allocations. Reused from Suppliers,
          Partners, Employees and Bank-Statements pages. */}
      <BankPaymentDialog
        open={showNewPayment}
        onOpenChange={setShowNewPayment}
        onCreated={() => { setShowNewPayment(false); load(); }}
      />

      {/* iter418 — Quick Transfer by account number */}
      <QuickTransferDialog
        open={showQuickTransfer}
        onOpenChange={setShowQuickTransfer}
        bankAccounts={accounts}
        onSuccess={() => { setShowQuickTransfer(false); load(); }}
      />

      {/* iter425 — Reconcile this whole month */}
      <ReconcileMonthDialog
        open={showReconcileMonth}
        onOpenChange={setShowReconcileMonth}
        onApplied={() => load()}
      />

      {/* iter426 — Scheduled auto-reconcile settings */}
      <AutoReconcileSettingsDialog
        open={showAutoReconSettings}
        onOpenChange={setShowAutoReconSettings}
      />

      {/* iter395j — Floating Action Button (FAB) for "+ New bank payment".
          Permanently accessible bottom-right, independent of the
          GettingStartedChecklist (which collapses once all activation
          steps are complete). Mirrors the POS FAB pattern users are
          already familiar with. */}
      <button
        type="button"
        onClick={() => setShowNewPayment(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3.5 rounded-full bg-foreground text-background shadow-2xl hover:scale-105 hover:shadow-foreground/30 active:scale-95 transition-all font-semibold text-sm group"
        title="Record a new bank payment to any supplier / employee / partner / customer"
        data-testid="banking-hub-fab-new-payment"
      >
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-background/20 group-hover:rotate-90 transition-transform">
          <Plus size={16} strokeWidth={2.5} />
        </span>
        <span className="hidden sm:inline">New bank payment</span>
      </button>

      {/* iter390 — Unlink modal. Renders only when the user clicks the
          amber Unlink button on a linked-row. Lists every downstream
          doc and offers two actions per link: Delete (removes the doc
          and detaches) or Detach only (keeps the doc, removes link). */}
      {unlinkRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !unlinkBusy && setUnlinkRow(null)}
          data-testid="unlink-dialog-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80dvh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="unlink-dialog"
          >
            <div className="px-5 py-4 border-b bg-amber-50 flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-amber-900">Linked records</div>
                <div className="text-xs text-amber-800/70 mt-0.5 inline-flex items-center gap-1">
                  Bank payment of <strong>SAR {fmt(unlinkRow.amount)}</strong> on {String(unlinkRow.ts || '').slice(0, 10)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => !unlinkBusy && setUnlinkRow(null)}
                className="text-amber-800 hover:text-amber-950 text-sm"
                data-testid="unlink-close"
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
              {unlinkLoading && <div className="text-sm text-muted-foreground">Loading linked records…</div>}
              {!unlinkLoading && unlinkLinks.length === 0 && (
                <div className="text-sm text-muted-foreground py-6 text-center">No remaining linked records.</div>
              )}
              {unlinkLinks.map((l) => (
                <div
                  key={`${l.collection}:${l.id}`}
                  className="rounded-xl border border-stone-200 bg-white px-4 py-3 flex items-start gap-3"
                  data-testid={`unlink-row-${l.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{l.name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 inline-flex items-center gap-2">
                      <span className="uppercase tracking-wide">{l.collection.replace('_', ' ')}</span>
                      {l.type && <><span>·</span><span>{l.type}</span></>}
                      <span>·</span>
                      <span>SAR {(l.amount || 0).toFixed(2)}</span>
                      {l.date && <><span>·</span><span>{String(l.date).slice(0, 10)}</span></>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => performUnlink(l, true)}
                      disabled={unlinkBusy}
                      className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-semibold disabled:opacity-50"
                      data-testid={`unlink-delete-${l.id}`}
                      title="Delete this downstream record (use when you linked the wrong chip)"
                    >
                      Delete record
                    </button>
                    <button
                      type="button"
                      onClick={() => performUnlink(l, false)}
                      disabled={unlinkBusy}
                      className="px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-800 text-[11px] font-semibold border border-stone-300 disabled:opacity-50"
                      data-testid={`unlink-detach-${l.id}`}
                      title="Just remove the bank link, keep the record (use when the record exists but shouldn't be tied to this bank txn)"
                    >
                      Detach only
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t text-[11px] text-muted-foreground bg-stone-50 leading-relaxed">
              <strong>Delete record</strong> removes the downstream document entirely (use when the chip click was wrong). <strong>Detach only</strong> keeps the document but unlinks the bank txn (use when the record is right but shouldn&apos;t be tied to this bank line).
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}


/**
 * InlineKindDropdown — lets the user reclassify a misclassified bank-payment
 * without having to delete and recreate the row. Appears as a tiny pencil
 * icon next to the kind pill; clicking opens a small menu of alternatives.
 *
 * Kind change CLEARS the payee_id since the target entity type changes —
 * user re-picks the right supplier/employee/etc. via the existing link flow.
 */
function InlineKindDropdown({ row, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // iter593 — Drop "Partner" from the kind dropdown when tenant has
  // opted out. Existing rows already tagged 'partner' still show their
  // label (KIND_META is untouched) — we only hide the option to ASSIGN
  // new rows to partner. Preserves historical data.
  const partnersEnabled = useFeatureFlagsStore(s => s.flags.enable_partner_entries);
  const options = [
    { v: 'supplier', l: 'Supplier' },
    { v: 'employee', l: 'Employee' },
    ...(partnersEnabled ? [{ v: 'partner', l: 'Partner' }] : []),
    { v: 'customer', l: 'Customer' },
    { v: 'expense_category', l: 'Expense Cat.' },
    { v: 'government', l: 'Government' },
    { v: 'other', l: 'Other' },
  ];
  const change = async (newKind) => {
    if (newKind === row.kind) { setOpen(false); return; }
    setSaving(true);
    try {
      await api.patch(`/banking/payments/${row.id}/kind`, { kind: newKind });
      toast.success(`Changed to ${newKind.replace(/_/g, ' ')}`);
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change kind');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="text-[10px] text-muted-foreground hover:text-foreground px-1 py-0.5 rounded hover:bg-muted/50 transition"
        title="Change kind"
        data-testid={`kind-change-${row.id}`}
        disabled={saving}
      >
        ✎
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-20 rounded-md border border-border bg-white dark:bg-stone-900 shadow-lg py-1 min-w-[140px]" data-testid={`kind-menu-${row.id}`}>
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => change(o.v)}
                disabled={saving}
                className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-muted/60 transition ${o.v === row.kind ? 'bg-muted/40 font-semibold' : ''}`}
                data-testid={`kind-option-${row.id}-${o.v}`}
              >
                {o.l} {o.v === row.kind ? '✓' : ''}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * InboxCountBanner — shows the number of unassigned bank-statement
 * transactions waiting in the Quick-Link inbox. Explains why the Banking
 * Hub totals can differ from the Bank Statements page totals (this page
 * counts only categorized rows, that page counts every line in the file).
 *
 * Hides itself when there are zero unassigned txns or while loading.
 */
function InboxCountBanner({ bankAccountId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get('/banking/inbox-count', { params: bankAccountId ? { bank_account_id: bankAccountId } : {} });
        if (!cancel) setData(r.data);
      } catch {
        // silent
      }
    })();
    return () => { cancel = true; };
  }, [bankAccountId]);

  if (!data || !data.count) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-900 p-3 text-xs flex items-start gap-2.5" data-testid="banking-hub-inbox-banner">
      <Inbox size={14} className="text-amber-600 mt-0.5 shrink-0" />
      <div className="text-amber-900 dark:text-amber-200 flex-1">
        <strong className="font-semibold">{data.count.toLocaleString()} untagged bank-statement transaction{data.count === 1 ? '' : 's'}</strong> waiting to be classified
        {data.total_in > 0 || data.total_out > 0 ? (
          <span className="text-amber-700 dark:text-amber-300">
            {' '}({data.total_in > 0 ? `+${data.total_in.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ''}
            {data.total_out > 0 ? ` -${data.total_out.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ''})
          </span>
        ) : null}
        . That's why these totals differ from the raw Bank Statements page.
      </div>
      <Link
        to="/banking-quick-link"
        className="shrink-0 px-2.5 py-1 rounded-md bg-amber-200 hover:bg-amber-300 text-amber-900 font-semibold text-[11px] transition-colors"
        data-testid="banking-hub-inbox-cta"
      >
        Tag now →
      </Link>
    </div>
  );
}

